import React from "react";
import { Box, Typography } from "@mui/material";
import GoogleCalendarIcon from "../../images/google-calendar.svg";

const STATUS_COLORS = {
    CONFIRMED: "#0087EE",
    PENDING: "#90CAF9",
    CANCELLED: "#E0E0E0",
    NOSHOW: "#FF8A65",
    DECLINED: "#E0E0E0"
};

const BLOCKED_COLOR = "#9E9E9E";
const COURSE_COLOR = "#2E7D32";
const GOOGLE_CAL_COLOR = "#4285F4";

/**
 * Renders a single booking or blocked-time overlay on the calendar grid.
 */
const CalendarEvent = React.memo(function CalendarEvent({
    event,
    type, // "booking" | "blocked"
    topOffset, // px from top of the cell container
    height, // px height
    colLeft, // CSS left value (e.g. "0%") for overlap layout
    colWidth, // CSS width value (e.g. "50%") for overlap layout
    onClick,
    onContextMenu,
    onMouseEnter,
    onMouseLeave
}) {
    const isBlocked = type === "blocked";
    const isGoogleCal = event.source === "googleCal";
    const isCourse = event.source === "course";
    const isPending = event.status === "PENDING" && !isGoogleCal && !isCourse;

    const getBackgroundColor = () => {
        if (isBlocked) return BLOCKED_COLOR;
        if (isGoogleCal) return GOOGLE_CAL_COLOR;
        if (isCourse) return COURSE_COLOR;
        if (event.courseInfo) return COURSE_COLOR;
        return STATUS_COLORS[event.status] || STATUS_COLORS.CONFIRMED;
    };

    const blockedNotes = isBlocked ? (event.notes || "").trim() : "";
    const hasBlockedNotes = Boolean(blockedNotes);

    const getLabel = () => {
        if (isBlocked) return "Blocked Time";
        if (isGoogleCal) return event.MetaData?.eventTitle || "Google Calendar";
        if (isCourse) {
            return event.servicetypeName || "Course";
        }
        const name = event.clientName || "";
        const service = event.servicetypeName || "";
        return name || service || "Booking";
    };

    const getSubLabel = () => {
        if (isBlocked) {
            return hasBlockedNotes
                ? blockedNotes
                : `${event.startTime} - ${event.endTime}`;
        }
        if (isGoogleCal) return event.MetaData?.address || `${event.startTime} - ${event.endTime}`;
        if (isCourse) {
            const ci = event.courseInfo;
            const session = ci ? `Session ${ci.sessionNo}/${ci.numberOfSessions}` : "";
            return session || `${event.startTime} - ${event.endTime}`;
        }
        if (isPending) return "Pending";
        const time = `${event.startTime} - ${event.endTime}`;
        if (event.clientName && event.servicetypeName) {
            return event.servicetypeName;
        }
        return time;
    };

    return (
        <Box
            onClick={(e) => {
                e.stopPropagation();
                onClick?.(event, type, e);
            }}
            onContextMenu={(e) => {
                e.stopPropagation();
                onContextMenu?.(event, type, e);
            }}
            onMouseEnter={(e) => onMouseEnter?.(event, type, e)}
            onMouseLeave={(e) => onMouseLeave?.(event, type, e)}
            sx={{
                position: "absolute",
                pointerEvents: "auto",
                top: `${topOffset}px`,
                left: colLeft || "2px",
                width: colWidth || "calc(100% - 4px)",
                height: `${height}px`,
                boxSizing: "border-box",
                minHeight: "20px",
                backgroundColor: isGoogleCal ? "#e0e0e0" : getBackgroundColor(),
                ...(isGoogleCal && {
                    backgroundImage:
                        "repeating-linear-gradient(135deg, transparent, transparent 4px, rgba(0,0,0,0.06) 4px, rgba(0,0,0,0.06) 8px)"
                }),
                borderRadius: "4px",
                padding: "2px 6px",
                cursor: "pointer",
                overflow: "hidden",
                zIndex: 2,
                opacity: isBlocked ? 0.7 : 0.9,
                border: isGoogleCal
                    ? "1px solid rgba(0,0,0,0.15)"
                    : isBlocked
                      ? "1px dashed rgba(0,0,0,0.3)"
                      : "1px solid rgba(0,0,0,0.08)",
                "&:hover": {
                    opacity: 1,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
                },
                transition: "opacity 0.15s, box-shadow 0.15s"
            }}
        >
            <Typography
                variant="caption"
                sx={{
                    color: isGoogleCal ? "rgba(0,0,0,0.8)" : "#fff",
                    fontWeight: 600,
                    fontSize: height > 25 ? "0.7rem" : "0.6rem",
                    lineHeight: 1.2,
                    display: "block",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                }}
            >
                {getLabel()}
            </Typography>
            {height > 22 && (
                <Typography
                    variant="caption"
                    sx={{
                        color: isGoogleCal
                            ? "rgba(0,0,0,0.55)"
                            : isBlocked && hasBlockedNotes
                              ? "rgba(255,255,255,0.95)"
                              : "rgba(255,255,255,0.85)",
                        fontSize:
                            isBlocked && hasBlockedNotes ? "0.65rem" : "0.6rem",
                        fontStyle:
                            isBlocked && hasBlockedNotes ? "italic" : "normal",
                        lineHeight: 1.25,
                        display: "-webkit-box",
                        WebkitLineClamp:
                            isBlocked && hasBlockedNotes ? 2 : 1,
                        WebkitBoxOrient: "vertical",
                        whiteSpace:
                            isBlocked && hasBlockedNotes ? "normal" : "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        wordBreak: "break-word"
                    }}
                >
                    {getSubLabel()}
                </Typography>
            )}
            {height > 34 && (!isBlocked || hasBlockedNotes) && (
                <Typography
                    variant="caption"
                    sx={{
                        color: isGoogleCal
                            ? "rgba(0,0,0,0.45)"
                            : "rgba(255,255,255,0.7)",
                        fontSize: "0.6rem",
                        lineHeight: 1.2,
                        display: "block",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                    }}
                >
                    {event.startTime} - {event.endTime}
                </Typography>
            )}
            {height > 46 && !isBlocked && event.location && (
                <Typography
                    variant="caption"
                    sx={{
                        color: isGoogleCal
                            ? "rgba(0,0,0,0.45)"
                            : "rgba(255,255,255,0.7)",
                        fontSize: "0.6rem",
                        lineHeight: 1.2,
                        display: "block",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                    }}
                >
                    {event.location}
                </Typography>
            )}
            {height > 58 && event.courseInfo && !isCourse && (
                <Typography
                    variant="caption"
                    sx={{
                        color: "rgba(255,255,255,0.75)",
                        fontSize: "0.6rem",
                        lineHeight: 1.2
                    }}
                >
                    Session {event.courseInfo.sessionNo}/
                    {event.courseInfo.numberOfSessions}
                </Typography>
            )}
            {isGoogleCal && (
                <Box
                    component="img"
                    src={GoogleCalendarIcon}
                    alt="Google Calendar"
                    sx={{
                        position: "absolute",
                        top: 3,
                        right: 3,
                        width: height > 30 ? 14 : 10,
                        height: height > 30 ? 14 : 10,
                        opacity: 0.9,
                        filter: "drop-shadow(0 0 1px rgba(0,0,0,0.3))"
                    }}
                />
            )}
            {height > 30 && isPending && (
                <Typography
                    variant="caption"
                    sx={{
                        color: "rgba(255,255,255,0.85)",
                        fontSize: "0.55rem",
                        lineHeight: 1.2,
                        fontStyle: "italic"
                    }}
                >
                    Pending
                </Typography>
            )}
        </Box>
    );
});

export default CalendarEvent;
